import type { WorkloadMetricsEntry, WorkloadMetricsInterval } from '@/lib/urm-api';
import { buildWorkloadLatencySummaries, type WorkloadLatencySummary } from './latency.logic';

export interface ApiTuple {
  key: string;
  resourceType: string;
  action: string;
  apiLabel: string;
  label: string;
}

function tupleKey(entry: WorkloadMetricsEntry): string {
  return JSON.stringify([entry.resource_type, entry.action.toLowerCase(), entry.api_label]);
}

/** Distinct ResourceType × Action × APILabel tuples present in the window, for
 * the breakdown selector. */
export function listApiTuples(intervals: WorkloadMetricsInterval[]): ApiTuple[] {
  const tuples = new Map<string, ApiTuple>();
  for (const interval of intervals) {
    for (const entry of interval.entries) {
      const key = tupleKey(entry);
      if (tuples.has(key)) continue;
      const action = entry.action.toLowerCase();
      tuples.set(key, {
        key,
        resourceType: entry.resource_type,
        action,
        apiLabel: entry.api_label,
        label: [entry.resource_type, action, entry.api_label].join(' · '),
      });
    }
  }
  return Array.from(tuples.values()).sort((left, right) => left.label.localeCompare(right.label));
}

/** Per-user rows for one chosen tuple: the opposite of the user-combined
 * tables. Reuses the shared latency grouping, grouped by username. */
export function buildPerUserBreakdown(
  intervals: WorkloadMetricsInterval[],
  selectedTupleKey: string,
): WorkloadLatencySummary[] {
  const filtered = intervals.map((interval) => ({
    ...interval,
    entries: interval.entries.filter((entry) => tupleKey(entry) === selectedTupleKey),
  }));
  return buildWorkloadLatencySummaries(filtered, ['username'])
    .filter((row) => row.totalRequests > 0)
    .sort((left, right) => left.label.localeCompare(right.label));
}
